/*! Copyright (c) Safe As Milk. All rights reserved. */

import Cart from "cart-store";
import { formatMoney } from "utils";

class CartThreshold extends HTMLElement {

  constructor() {
    super();

    this.unsubscribe = null;
    this.previousActiveIndex = -1;

    this.confettiElement = null;
    this.confettiCanvas = null;
    this.confettiAnimation = null;
    this.confettiTimeout = null;
  }

  connectedCallback() {

    this.confettiElement = this.querySelector(
      "[data-threshold-confetti]"
    );

    this.confettiCanvas =
      this.confettiElement?.querySelector("canvas");

    this.fill = this.querySelector(
      "[data-threshold-fill]"
    );

    this.unlocked = this.querySelector(
      "[data-threshold-unlocked]"
    );

    this.message = this.querySelector(
      "[data-threshold-message]"
    );

    this.tiers = Array.from(
      this.querySelectorAll(
        "[data-threshold-tier]"
      )
    )
      .map((element) => ({
        element,
        threshold: Number(
          element.dataset.threshold
        ),
        discount: Number(
          element.dataset.discount
        )
      }))
      .filter(
        (tier) => tier.threshold > 0
      )
      .sort(
        (a, b) =>
          a.threshold - b.threshold
      );

    if (
      !this.fill ||
      !this.unlocked ||
      !this.message ||
      !this.tiers.length
    ) {
      return;
    }

    /*
     * Initial cart value.
     *
     * IMPORTANT:
     * Use original_total_price so that
     * threshold is calculated BEFORE
     * discount is deducted.
     *
     * Example:
     * Product total = ₹3600
     * Discount = ₹360
     * Subtotal = ₹3240
     *
     * Threshold should still see ₹3600.
     */

    const initialTotal =
      Number(
        this.dataset.cartOriginalTotal ||
        this.dataset.cartTotal ||
        0
      );

    this.update(initialTotal, false);

    /*
     * Venue native cart store
     */

    this.unsubscribe = Cart.subscribe(
      (state) => {

        if (!state || !state.cart) {
          return;
        }

        /*
         * Use ORIGINAL cart value.
         *
         * Shopify cart:
         * original_total_price = before discount
         * total_price = after discount
         */

        const originalTotal =
          Number(
            state.cart.original_total_price ||
            state.cart.items_subtotal_price ||
            state.cart.total_price ||
            0
          );

        this.update(
          originalTotal,
          true
        );
      }
    );
  }


  disconnectedCallback() {

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    if (this.confettiTimeout) {
      clearTimeout(
        this.confettiTimeout
      );
    }

    if (this.confettiAnimation) {
      try {
        this.confettiAnimation.stop();
      } catch (error) {
        // Ignore animation cleanup errors
      }

      this.confettiAnimation = null;
    }
  }


  /*
   * ============================
   * CONFETTI
   * ============================
   */

  async showConfetti() {

    if (
      !this.confettiElement ||
      !this.confettiCanvas
    ) {
      return;
    }

    /*
     * Remove previous animation
     */

    if (this.confettiTimeout) {
      clearTimeout(
        this.confettiTimeout
      );
    }

    if (this.confettiAnimation) {
      try {
        this.confettiAnimation.stop();
      } catch (error) {
        // Ignore
      }

      this.confettiAnimation = null;
    }

    try {

      const { DotLottie } =
        await import("dotlottie");

      this.confettiElement.hidden = false;

      this.confettiAnimation =
        new DotLottie({
          canvas:
            this.confettiCanvas,
          loop: false,
          autoplay: true,
          src:
            this.confettiElement
              .dataset
              .animationUrl
        });

      /*
       * Show for approximately 2 seconds
       */

      this.confettiTimeout =
        setTimeout(() => {

          this.confettiElement.hidden =
            true;

          if (
            this.confettiAnimation
          ) {
            try {
              this.confettiAnimation.stop();
            } catch (error) {
              // Ignore
            }

            this.confettiAnimation =
              null;
          }

        }, 2000);

    } catch (error) {

      console.warn(
        "Cart threshold confetti could not load:",
        error
      );

    }
  }


  /*
   * ============================
   * TIER PULSE
   * ============================
   */

  pulseTier(tier) {

    if (!tier?.element) {
      return;
    }

    /*
     * Remove old animation first
     * so it can replay every time.
     */

    tier.element.classList.remove(
      "is-pulsing"
    );

    /*
     * Force browser reflow
     * so animation restarts.
     */

    void tier.element.offsetWidth;

    tier.element.classList.add(
      "is-pulsing"
    );

    /*
     * Remove after animation
     */

    setTimeout(() => {

      tier.element.classList.remove(
        "is-pulsing"
      );

    }, 2000);
  }


  /*
   * ============================
   * UPDATE
   * ============================
   */

  update(total, allowAnimation = true) {

    let activeIndex = -1;

    /*
     * Find highest unlocked tier
     */

    this.tiers.forEach(
      (tier, index) => {

        const isReached =
          total >= tier.threshold;

        tier.element.classList.toggle(
          "is-active",
          isReached
        );

        if (isReached) {
          activeIndex = index;
        }
      }
    );


    /*
     * Detect newly unlocked tier
     */

    if (
      allowAnimation &&
      activeIndex >
        this.previousActiveIndex
    ) {

      /*
       * If Tier 1/2/3 has just
       * been reached
       */

      if (activeIndex >= 0) {

        const unlockedTier =
          this.tiers[activeIndex];

        /*
         * Pulse marker + tier
         */

        this.pulseTier(
          unlockedTier
        );

        /*
         * Show confetti
         */

        this.showConfetti();
      }
    }


    /*
     * Save current tier
     */

    this.previousActiveIndex =
      activeIndex;


    /*
     * Update progress
     */

    this.updateProgress(
      total,
      activeIndex
    );


    /*
     * Update messages
     */

    this.updateMessage(
      total,
      activeIndex
    );
  }


  /*
   * ============================
   * PROGRESS
   * ============================
   */

  updateProgress(
    total,
    activeIndex
  ) {

    const tierCount =
      this.tiers.length;

    const segment =
      100 / tierCount;

    let progress = 0;


    /*
     * Before first tier
     */

    if (activeIndex === -1) {

      const firstTier =
        this.tiers[0];

      const ratio =
        firstTier.threshold > 0
          ? total /
            firstTier.threshold
          : 0;

      progress =
        Math.min(
          Math.max(
            ratio,
            0
          ),
          1
        ) * segment;
    }


    /*
     * Between tiers
     */

    else if (
      activeIndex <
      tierCount - 1
    ) {

      const currentTier =
        this.tiers[activeIndex];

      const nextTier =
        this.tiers[
          activeIndex + 1
        ];

      const startPosition =
        (activeIndex + 1) *
        segment;

      const range =
        nextTier.threshold -
        currentTier.threshold;

      const amount =
        total -
        currentTier.threshold;

      const ratio =
        range > 0
          ? amount / range
          : 0;

      progress =
        startPosition +
        Math.min(
          Math.max(
            ratio,
            0
          ),
          1
        ) * segment;
    }


    /*
     * Last tier reached
     */

    else {

      progress = 100;
    }


    /*
     * Update fill
     */

    this.fill.style.width =
      `${Math.min(
        progress,
        100
      )}%`;
  }


  /*
   * ============================
   * MESSAGE
   * ============================
   */

  updateMessage(
    total,
    activeIndex
  ) {

    /*
     * Find next tier
     */

    const nextTier =
      activeIndex <
      this.tiers.length - 1
        ? this.tiers[
            activeIndex + 1
          ]
        : null;


    /*
     * TOP BLACK HEADING
     */

    if (activeIndex >= 0) {

      this.unlocked.textContent =
        `${this.tiers[activeIndex].discount}% OFF unlocked`;

    } else {

      this.unlocked.textContent =
        "Unlock your discount";
    }


    /*
     * NEXT TIER MESSAGE
     */

    if (nextTier) {

      const remaining =
        Math.max(
          nextTier.threshold -
            total,
          0
        );

      const formattedAmount =
        formatMoney(
          remaining,
          window.theme.money_format
        ).replace(
          /[.,]00$/,
          ""
        );

      this.message.innerHTML =
        `Add items worth <strong>${formattedAmount}</strong> more to unlock <strong>${nextTier.discount}% OFF</strong>`;

    } else {

      /*
       * All tiers completed
       */

      this.message.innerHTML =
        "<strong>Maximum discount unlocked!</strong>";
    }
  }
}


if (
  !customElements.get(
    "cart-threshold"
  )
) {

  customElements.define(
    "cart-threshold",
    CartThreshold
  );
}